// Marketing autopilot: types, the mk.* calls, and Hawaii-time helpers.
// Every time on these pages is shown in HST (Pacific/Honolulu, UTC−10, no
// daylight saving), because that is where the posting windows live.
import { call } from './backbarApi';

export const PILLARS = [
  'scalp_science', 'extension_integrity', 'apprenticeship', 'mentorship', 'salon_leadership',
  'app_feature', 'hi_specific', 'myth_vs_fact', 'study_tip',
] as const;
export type Pillar = (typeof PILLARS)[number];

export const PILLAR_LABELS: Record<Pillar, string> = {
  scalp_science: 'Scalp science',
  extension_integrity: 'Extension integrity',
  apprenticeship: 'Apprenticeship',
  mentorship: 'Mentorship',
  salon_leadership: 'Salon leadership',
  app_feature: 'App feature',
  hi_specific: 'Hawaii',
  myth_vs_fact: 'Myth vs. fact',
  study_tip: 'Study tip',
};
export const pillarLabel = (p: string | null | undefined) => (p && (PILLAR_LABELS as Record<string, string>)[p]) || p || 'No pillar';

export const THREADS_MAX = 500;

export type ApprovalState = 'draft' | 'approved' | 'rejected';

export type Post = {
  id: string;
  platform: string;
  body: string;
  link_url: string | null;
  pillar: string | null;
  source: string | null;
  source_ref: string | null;
  approval_state: ApprovalState;
  auto_post: boolean;
  scheduled_at: string | null;
  posted_at: string | null;
  external_id: string | null;
  permalink: string | null;
  error: string | null;
  attempts: number;
  engagement: Record<string, number> | null;
  created_at: string;
  updated_at: string;
};

/** What the row reads as. Same rules as mk_queue_list / mk_stats (307):
 *  posted = posted_at set; failed = not posted and an error is recorded. */
export type DisplayState = ApprovalState | 'posted' | 'failed';
export function displayState(p: Post): DisplayState {
  if (p.posted_at) return 'posted';
  if (p.error) return 'failed';
  return p.approval_state;
}

export type ThreadsHealth = {
  status?: 'unconfigured' | 'ok' | 'blocked' | 'error' | string;
  username?: string | null;
  user_id?: string | null;
  followers?: number | null;
  error?: string | null;
  message?: string | null;
  checked_at?: string | null;
  [k: string]: unknown;
};

export type BrainRun = {
  id?: string;
  started_at: string | null;
  finished_at: string | null;
  status: 'ok' | 'failed' | 'skipped' | string;
  summary: string | null;
  counts?: Record<string, number> | null;
  host?: string | null;
  error?: string | null;
};

export type DayCount = { day: string; count: number; approved: number; draft: number; posted: number };

export type Stats = {
  counts: { draft: number; approved: number; rejected: number; posted: number; failed: number };
  next7: DayCount[];
  lastPost: { body?: string; posted_at?: string | null; permalink?: string | null } | null;
  health: ThreadsHealth;
  lastBrainRun: BrainRun | null;
  nextPost: { body?: string; scheduled_at?: string | null } | null;
  inboxPending: number | null;
};

type Loose = Record<string, unknown>;
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0) || 0);

/** mk_stats() → one shape the pages can trust, whatever keys the RPC chose. */
export function normalizeStats(raw: unknown): Stats {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Loose;
  const c = ((r.counts ?? r.by_state ?? r.state_counts ?? {}) as Loose);
  const next = (r.next_7_days ?? r.next7 ?? r.next_days ?? r.upcoming ?? []) as unknown[];
  const health = ((r.threads_health ?? r.health ?? {}) as ThreadsHealth) || {};
  return {
    counts: {
      draft: num(c.draft),
      approved: num(c.approved ?? c.queued),
      rejected: num(c.rejected),
      posted: num(c.posted),
      failed: num(c.failed),
    },
    next7: (Array.isArray(next) ? next : []).map((d) => {
      const o = d as Loose;
      const approved = num(o.approved), draft = num(o.draft), posted = num(o.posted);
      return { day: String(o.day ?? o.date ?? ''), approved, draft, posted, count: o.count != null ? num(o.count) : approved + draft + posted };
    }),
    lastPost: (r.last_post ?? r.lastPost ?? null) as Stats['lastPost'],
    health: health.status ? health : { status: 'unconfigured' },
    lastBrainRun: (r.last_brain_run ?? r.brain ?? null) as BrainRun | null,
    nextPost: (r.next_post ?? null) as Stats['nextPost'],
    inboxPending: r.inbox && typeof r.inbox === 'object' ? num((r.inbox as Loose).pending) : null,
  };
}

export type InboxItem = {
  id: string;
  platform: string;
  external_id: string;
  parent_external_id: string | null;
  author_handle: string | null;
  text: string | null;
  received_at: string | null;
  classification: 'benign' | 'sensitive' | 'spam' | null;
  draft_reply: string | null;
  reply_state: 'pending' | 'approved' | 'posted' | 'skipped';
  posted_at: string | null;
  parent: { body: string; permalink: string | null } | null;
};

export type Cadence = { threads: { per_day: number; windows_hst: [string, string][] } };
export type ReplyCaps = { per_day: number; per_author_per_day: number };
export type MkSettings = {
  posting_cadence?: Cadence;
  pillar_weights?: Partial<Record<Pillar, number>>;
  queue_horizon_days?: number;
  reply_caps?: ReplyCaps;
  threads_health?: ThreadsHealth;
  [k: string]: unknown;
};

export const mk = {
  stats: async () => normalizeStats((await call<{ stats: unknown }>('mk.stats')).stats),
  queue: async (p: { state?: string; from?: string; to?: string; limit?: number } = {}) =>
    (await call<{ posts: Post[] }>('mk.queue_list', p)).posts ?? [],
  update: (id: string, patch: { body?: string; scheduled_at?: string; link_url?: string; pillar?: string }) =>
    call('mk.post_update', { id, ...patch }),
  setState: (ids: string[], state: ApprovalState) => call('mk.post_set_state', { ids, state }),
  bulkSchedule: (ids: string[], start: string) => call('mk.bulk_schedule', { ids, start }),
  inbox: async (state = 'pending') => (await call<{ items: InboxItem[] }>('mk.inbox_list', { state })).items ?? [],
  inboxSet: (id: string, p: { classification?: string; draft_reply?: string; reply_state?: string }) =>
    call('mk.inbox_set', { id, ...p }),
  settings: () => call<{ settings: MkSettings; updated_at: Record<string, string>; token_issued_at: string | null }>('mk.settings_get'),
  saveSetting: (key: string, value: unknown) => call('mk.settings_set', { key, value }),
  healthNow: () =>
    call<{ ok: boolean; reason: string | null; health: ThreadsHealth; health_updated_at: string | null }>('mk.health_now'),
};

// ── Hawaii time ───────────────────────────────────────────────────────────
export const HST = 'Pacific/Honolulu';
const HST_OFFSET_MS = -10 * 3600_000;

/** "2026-10-27" for the HST calendar day of an instant. */
export function hstDayKey(iso: string | Date): string {
  const t = (iso instanceof Date ? iso.getTime() : Date.parse(iso)) + HST_OFFSET_MS;
  return new Date(t).toISOString().slice(0, 10);
}
export function hstToday(): string {
  return hstDayKey(new Date());
}
/** "2026-10-27" + n days. */
export function addDays(key: string, n: number): string {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/** "8:30 AM" in HST. */
export function hstTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: HST, hour: 'numeric', minute: '2-digit' });
}
/** "Tue, Oct 27" for a day key. */
export function dayLabel(key: string, withYear = false): string {
  return new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
}
/** "Oct 27, 8:30 AM HST" */
export function hstDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${new Date(iso).toLocaleString('en-US', { timeZone: HST, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} HST`;
}
/** ISO → the value of an <input type="datetime-local"> read as HST. */
export function toHstInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(Date.parse(iso) + HST_OFFSET_MS).toISOString().slice(0, 16);
}
/** <input type="datetime-local"> value typed as HST → ISO with the offset. */
export function fromHstInput(v: string): string {
  return `${v.length === 16 ? `${v}:00` : v}-10:00`;
}

/** Characters as Threads counts them (code points). */
export const chars = (s: string) => [...s].length;
